<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Offcanvas,
    Row
  } from '@sveltestrap/sveltestrap';

  import { CampaignCard } from '@client/components/campaign-card';
  import { MultiSelectFilter } from '@client/components/filters/multiselect';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { PerformanceInsights } from '@client/components/performance-insights';
  import { Spinner } from '@client/components/spinner';
  import { Table } from '@client/components/table';

  import { ActionsCell, BannerCell, StatusCell } from '@client/pages/marketing/campaigns/manage/cell-formatters';

  import { appStore } from '@client/stores/app';

  import { formatExportRate } from '@client/utils/banners';
  import { buildExportExtensionCells, determineCampaignStatus, NEW_EXPORT_HEADER_KEYS } from '@client/utils/campaigns';
  import { csvField, downloadFile } from '@client/utils/csv';
  import { QueryParams } from '@client/utils/query-params';

  import { CAMPAIGN_FILTERS } from '@client/enums';

  import { formatDate, getDateFromToday } from '@common/utils/dates';
  import { debounce } from '@common/utils/debounce';
  import { merge } from '@common/utils/objects';

  import { CampaignCell, MetricsCell } from './cell-formatters';
  import { RowExpansion } from './row-expansion';

  const { basepath } = getContext('config');

  let cache = {};
  let filtered = [];
  let updating = false;
  let sortDirection = -1;
  let sortKey = 'reach';
  let loading = false;
  let windowWidth = window.innerWidth;

  let expanded = [];
  let integrations = [];
  let targeting = {};

  let selectedItems = [];
  let selectedData = [];

  let selectedItemField = 'campaigns';
  let includeEvergreen = false;
  let filters = merge({}, CAMPAIGN_FILTERS);

  let update = async () => {};

  // Performance pre-filters campaigns to the selected date window, so Upcoming (future start)
  // can never survive the window and is intentionally omitted to keep the control honest.
  let statusFilters = {
    'active-scheduled': { label: 'label.ACTIVE', value: 'active-scheduled', enabled: false },
    'active-targeting': { label: 'label.ACTIVE', value: 'active-targeting', enabled: false },
    expired: { label: 'label.CAMPAIGN_EXPIRED', value: 'expired', enabled: false },
    inactive: { label: 'label.INACTIVE', value: 'inactive', enabled: false },
    paused: { label: 'label.PAUSED', value: 'paused', enabled: false }
  };

  const onStatusFilterChange = () => {
    update(true);
  };

  filters.dateRange.startDate = QueryParams.get('startDate') || formatDate(getDateFromToday(59), 'MM/dd/yyyy');
  filters.dateRange.endDate = QueryParams.get('endDate') || formatDate(getDateFromToday(0), 'MM/dd/yyyy');
  filters.sorting.key = QueryParams.get('sortKey') || sortKey;
  filters.sorting.dir = QueryParams.get('sortDir') || sortDirection === -1 ? 'asc' : 'desc';

  let startDate = '';
  let endDate = '';

  const onActionUpdate = async (campaignObject) => {
    const currentIndex = selectedData.findIndex(({ _id }) => _id === campaignObject._id);
    selectedData.splice(currentIndex, 1, campaignObject);
    campaignObject = determineCampaignStatus(campaignObject, targeting, integrations);
    selectedData = [...selectedData];
  };

  let columns = [
    {
      key: 'name',
      title: $format('label.CREATIVE'),
      value: ({ rulename }) => rulename,
      renderComponent: BannerCell,
      headerClass: 'banner-cell',
      sortable: true
    },
    {
      key: 'schedule',
      title: $format('label.CAMPAIGN'),
      headerClass: 'width-30p',
      value: ({ schedule }) => {
        if (!schedule) {
          return '-';
        }

        return `${formatDate(schedule.startDate, 'EEE, MMM d, yy @ h a')} - ${formatDate(
          schedule.endDate,
          'EEE, MMM d, yy @ h a'
        )}`;
      },
      renderComponent: CampaignCell,
      sortable: true
    },
    {
      key: 'deliveries',
      title: $format('label.DELIVERIES'),
      headerClass: 'width-25p',
      sortable: true,
      value: ({ metrics }) => metrics?.totals?.ad || 0,
      renderComponent: {
        component: MetricsCell,
        props: {
          cache,
          filters,
          which: 'ad'
        }
      }
    },
    {
      key: 'impressions',
      title: $format('label.IMPRESSIONS'),
      headerClass: 'width-25p',
      sortable: true,
      value: ({ metrics }) => metrics?.totals?.combinedViews || 0,
      renderComponent: {
        component: MetricsCell,
        props: {
          cache,
          filters,
          which: 'combinedViews'
        }
      }
    },
    {
      key: 'clicks',
      title: $format('label.CLICKS'),
      headerClass: 'width-25p',
      sortable: true,
      value: ({ metrics }) => metrics?.totals?.combinedClicks || 0,
      renderComponent: {
        component: MetricsCell,
        props: {
          cache,
          filters,
          which: 'combinedClicks'
        }
      }
    },
    {
      key: 'ctr',
      title: 'CTR',
      headerClass: 'width-25p',
      sortable: true,
      value: ({ metrics }) => metrics?.totals?.combinedCtr ?? -1,
      comparator: 'float',
      renderComponent: {
        component: MetricsCell,
        props: {
          cache,
          filters,
          which: 'combinedCtr'
        }
      }
    },
    {
      key: 'ctrr',
      title: 'CTRR',
      headerClass: 'width-25p',
      sortable: true,
      value: ({ metrics }) => metrics?.totals?.ctrr ?? -1,
      comparator: 'float',
      renderComponent: {
        component: MetricsCell,
        props: {
          cache,
          filters,
          which: 'combinedCtrr'
        }
      }
    },
    {
      key: 'reach',
      title: $format('label.REACH'),
      headerClass: 'width-25p',
      sortable: true,
      value: ({ metrics }) => metrics?.totals?.uniques || 0,
      renderComponent: {
        component: MetricsCell,
        props: {
          cache,
          filters,
          which: 'uniques'
        }
      }
    },
    {
      key: 'activeStatus',
      title: $format('label.STATUS'),
      headerClass: 'status-cell',
      value: ({ activeStatus }) => activeStatus,
      renderComponent: StatusCell,
      sortable: true
    },
    {
      key: 'actions',
      title: '',
      value: '',
      headerClass: 'actions-cell',
      renderComponent: {
        component: ActionsCell,
        props: {
          isPerformance: true,
          update: onActionUpdate
        }
      }
    }
  ];

  let selectedMessageData = null;
  let showMessageDetails = false;

  const onMessageSelect = async (campaign) => {
    selectedMessageData = null;
    selectedMessageData = campaign;
    showMessageDetails = true;
  };

  const toggleMessageDetails = () => {
    showMessageDetails = !showMessageDetails;
  };

  const onResize = debounce(() => {
    windowWidth = window.innerWidth;
  }, 150);

  const toggleEvergreen = () => {
    includeEvergreen = !includeEvergreen;
  };

  const onExportCampaigns = async (isAll) => {
    updating = true;
    exporting = true;

    const headers = [
      $format('label.REPORTING_PERIOD'),
      $format('label.CAMPAIGN'),
      $format('label.DAYS_LIVE'),
      $format('label.TYPE'),
      $format('label.DELIVERIES'),
      $format('label.IMPRESSIONS'),
      $format('label.CLICKS'),
      $format('label.REACH'),
      $format('label.CLICK_THROUGH_RATE'),
      $format('label.CLICK_TO_RECIPIENT_RATE'),
      $format('label.TAGS'),
      $format('label.BASIC_VIEWS'),
      $format('label.BASIC_CLICKS'),
      $format('label.BASIC_CLICK_THROUGH_RATE'),
      $format('label.URL'),
      ...NEW_EXPORT_HEADER_KEYS.map((key) => $format(key)),
      $format('label.TOTAL_IMPRESSIONS'),
      $format('label.TOTAL_CLICKS'),
      $format('label.TOTAL_CTR')
    ];

    let rows = [];

    const reportingPeriod =
      formatDate(filters.dateRange.startDate, 'MM/dd/yyyy') + '-' + formatDate(filters.dateRange.endDate, 'MM/dd/yyyy');

    // Re-decorate any row missing status flags (e.g. a compare/re-derive path row) so the Status
    // column is never blank; the primary load path decorates via PerformanceInsights. See Risk R5.
    const exportRows = selectedData.map((campaign) =>
      campaign.active === undefined && campaign.paused === undefined
        ? determineCampaignStatus(campaign, targeting, integrations)
        : campaign
    );

    rows = exportRows.map((campaign) => {
      return [
        reportingPeriod,
        campaign.rulename,
        campaign.metrics?.totals.dayslive,
        campaign.adtype,
        campaign.metrics?.totals?.ad || 0,
        campaign.metrics?.totals?.adview || 0,
        campaign.metrics?.totals?.adclick || 0,
        campaign.metrics?.totals?.uniques || 0,
        formatExportRate(campaign.metrics?.totals?.ctr),
        formatExportRate(campaign.metrics?.totals?.ctrr),
        campaign.tags.map((tag) => tag.text).join('; '),
        campaign.metrics?.totals?.basicadview || 0,
        campaign.metrics?.totals?.basicadclick || 0,
        formatExportRate(campaign.metrics?.totals?.basicCtr),
        campaign.previewURL || '',
        ...buildExportExtensionCells(campaign, $format),
        campaign.metrics?.totals?.combinedViews || 0,
        campaign.metrics?.totals?.combinedClicks || 0,
        formatExportRate(campaign.metrics?.totals?.combinedCtr)
      ];
    });

    const csvContent = [headers, ...rows].map((e) => e.map(csvField).join(',')).join('\n');
    const filename = `performance-report(${reportingPeriod})`;

    exporting = false;
    updating = false;

    downloadFile(csvContent, `${filename}-${Date.now()}`);
  };

  const onExportClicks = () => {};

  let exporting = false;
  $: tabletView = windowWidth <= 905;
  const sortData = (data) => data.sort((a, b) => b?.metrics?.totals?.uniques - a?.metrics?.totals?.uniques);
</script>

<div id="marketing-performance" class="page">
  <Loader backdrop show={updating} />

  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/campaigns" use:link>{$format('label.MARKETING')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.PERFORMANCE')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="4" sm="6" md="6" lg="7">
          <h1 class="page-title">{$format('label.PERFORMANCE')}</h1>
        </Col>
        <Col xs="8" sm="6" md="6" lg="5" class="text-right">
          <div class="d-flex align-items-center justify-content-end">
            <div
              class="feature-toggle me-3"
              role="button"
              tabindex="0"
              class:active={includeEvergreen}
              on:click={toggleEvergreen}
            >
              <div class="dot">
                <i class:os-icon-circle={!includeEvergreen} class:os-icon-circle-filled={includeEvergreen} />
              </div>
              <span>
                {#if $appStore.mobile}
                  {$format('label.INCLUDE_EVERGREEN_SHORT')}
                {:else}
                  {$format('label.INCLUDE_EVERGREEN')}
                {/if}
              </span>
            </div>
            <div class="me-3">
              <MultiSelectFilter
                bind:filter={statusFilters}
                onClick={onStatusFilterChange}
                param="status"
                label={$format('label.STATUS')}
                maxHeight="400px"
              >
                <span class="badge-{config.value}" slot="label" let:config>
                  {$format(config.label)}
                </span>
              </MultiSelectFilter>
            </div>
            <Dropdown id="export-dropdown">
              <DropdownToggle class="export-dropdown" disabled={!filtered.length || exporting}>
                {#if exporting}
                  <Spinner styles="border-width: 2px; color: #0087ff; height: 14px; width: 14px;" show inline />
                {:else}
                  <span>{$format('label.EXPORT')}</span>
                {/if}
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem on:click={onExportCampaigns}>
                  {@html $format('label.EXPORT_X_TYPE', {
                    values: {
                      total: Number(selectedData.length).toLocaleString(),
                      type: $format('label.CAMPAIGNS')
                    }
                  })}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <PerformanceInsights
      bind:filtered
      bind:selectedData
      bind:selectedItems
      bind:selectedItemField
      bind:startDate={filters.dateRange.startDate}
      bind:endDate={filters.dateRange.endDate}
      bind:targeting
      bind:integrations
      bind:updating
      bind:cache
      bind:includeEvergreen
      bind:update
      {statusFilters}
      isEditView={false}
    />

    {#if $appStore.mobile || tabletView}
      <div class="campaign-cards">
        {#each sortData(selectedData) as campaign}
          <CampaignCard
            {campaign}
            scopedMetrics={campaign.metrics ?? null}
            {cache}
            {onActionUpdate}
            {onMessageSelect}
          />
        {/each}
      </div>

      {#if showMessageDetails}
        <Offcanvas id="performance-offcanvas" isOpen={showMessageDetails} toggle={toggleMessageDetails} placement="end">
          <Loader show={loading} backdrop="true" />
          <div slot="header" class="offcanvas-heading">
            <Icon name="share-2" />
            <span>{selectedMessageData.rulename}</span>
          </div>
          <RowExpansion bind:row={selectedMessageData} showRange days={30} />
        </Offcanvas>
      {/if}
    {:else}
      <div class="campaigns-table">
        <Table
          id="campaigns-table"
          classNameRowExpanded="expanded-row"
          classNameExpandedContent="expanded-content"
          expandRowKey="_id"
          expandOnRowClick={false}
          showExpandIcon={true}
          expandSingle={true}
          bind:rows={selectedData}
          bind:columns
          bind:expanded
          bind:sortBy={sortKey}
          bind:sortOrder={sortDirection}
        >
          <div slot="expanded" let:row>
            {#if expanded}
              <RowExpansion {row} showRange days={30} />
            {/if}
          </div>
        </Table>
      </div>
    {/if}
  </div>
</div>

<svelte:window on:resize={onResize} />

<style lang="scss" src="./performance.scss"></style>
